import { createSelector, createSlice } from '@reduxjs/toolkit';

import { selectorSearchById } from '../Search/SearchSlice';

import {
  initialItemData,
  initialRebuzzIn,
  initialRssFeed,
} from './constants/initialStates';
import { initialSubscriptions } from '../Item/Subscription/constants/initialSubscriptions';
import { ITEM_SELECT_TYPE } from '../Item/BulkActions/constants/itemSelectType';

import { generateItemCIDKey } from './utils/generateItemCIDKey';
import { generateThreadItemId } from './utils/generateThreadItemId';
import { handleCheckBulkSelection } from './utils/handleCheckBulkSelection';
import { INITIAL_LOADING_STATE } from '../../utils/asyncThunkRequest';

import { changeItemCommentableExtraReducers } from './asyncThunk/setItemCommentable';
import { addItemReactionExtraReducers } from './asyncThunk/addItemReaction';
import { changeItemPrivacyExtraReducer } from './asyncThunk/changeItemPrivacy';
import { changeItemRebuzzableExtraReducers } from './asyncThunk/changeItemRebuzzable';
import { checkForNewBuzzesExtraReducers } from './asyncThunk/checkForNewBuzzes';
import { createItemExtraReducers } from './asyncThunk/createItem';
import { editItemReactionExtraReducers } from './asyncThunk/editItemReaction';
import { fetchItemExtraReducers } from './asyncThunk/fetchItem';
import { fetchItemsExtraReducers } from './asyncThunk/fetchItems';
import { fetchItemRenderedExtraReducers } from './asyncThunk/itemRendered';
import { fetchLayoutExtraReducers } from './asyncThunk/fetchLayout';
import { fetchPinnedItemsExtraReducers } from './asyncThunk/fetchPinnedItems';
import { fetchTimelineExtraReducers } from './asyncThunk/fetchTimeline';
import { addItemBookmarkExtraReducers } from './asyncThunk/addItemBookmark';
import { removeItemBookmarkExtraReducers } from './asyncThunk/removeItemBookmark';
import { removeItemExtraReducers } from './asyncThunk/removeItem';
import { itemPinnedExtraReducers } from './asyncThunk/setItemPinned';
import { updateItemExtraReducers } from './asyncThunk/updateItem';
import { fetchRSSLinkExtraReducers } from './asyncThunk/fetchRSSLink';
import { createRssLinkExtraReducers } from './asyncThunk/createRssLink';
import { createSubscriptionExtraReducers } from './asyncThunk/createSubscription';
import { fetchSubscriptionExtraReducers } from './asyncThunk/fetchSubscription';
import { fetchItemLockedContentExtraReducers } from './asyncThunk/fetchItemLockedContent';

export { setItemCommentable } from './asyncThunk/setItemCommentable';
export { addItemReaction } from './asyncThunk/addItemReaction';
export { changeItemPrivacy } from './asyncThunk/changeItemPrivacy';
export { changeItemRebuzzable } from './asyncThunk/changeItemRebuzzable';
export { checkForNewBuzzes } from './asyncThunk/checkForNewBuzzes';
export { createItem } from './asyncThunk/createItem';
export { editItemReaction } from './asyncThunk/editItemReaction';
export { fetchItem } from './asyncThunk/fetchItem';
export { fetchItems } from './asyncThunk/fetchItems';
export { fetchItemRendered } from './asyncThunk/itemRendered';
export { fetchPinnedItems } from './asyncThunk/fetchPinnedItems';
export { fetchTimeline } from './asyncThunk/fetchTimeline';
export { addItemBookmark } from './asyncThunk/addItemBookmark';
export { removeItemBookmark } from './asyncThunk/removeItemBookmark';
export { setItemPinned } from './asyncThunk/setItemPinned';
export { removeItem } from './asyncThunk/removeItem';
export { updateItem } from './asyncThunk/updateItem';
export { fetchRSSLink } from './asyncThunk/fetchRSSLink';
export { createRssLink } from './asyncThunk/createRssLink';
export { updateRSSLink } from './asyncThunk/updateRSSLink';
export { createSubscription } from './asyncThunk/createSubscription';
export { fetchSubscription } from './asyncThunk/fetchSubscription';
export { fetchItemLockedContent } from './asyncThunk/fetchItemLockedContent';
export { createThread } from './asyncThunk/createThread';
export { addBuzzToThread } from './asyncThunk/addBuzzToThread';

const INITIAL_STATE = {
  items: {
    item: {
      byId: {},
      allIds: [],
      /*
      {
        "$orgId/$streamId/$channelId/$streamItemId": {
          loading: REQUEST.idle,
          currentRequestId: undefined,
          error: null
        }
      }
      */
      request: {},
      actions: {
        create: {
          /*
          {
            "$orgId/$streamId/$channelId": {
              loading: REQUEST.idle,
              currentRequestId: undefined,
              error: null
            }
          }
          */
          request: {},
        },
        bookmark: {
          request: {},
        },
        rebuzz: {
          request: {},
        },
        update: {
          request: {},
        },
        reaction: {
          request: {},
        },
        delete: {
          request: {},
        },
        check: {
          request: {},
        },
      },
    },
    rendered: {
      byId: {},
      request: {},
    },
    updatedILayoutItem: {
      // [anchorItemId]: layoutItem
    },
    lockedContent: {
      byId: {},
      request: {},
    },
    layouts: {
      byId: {},
      request: {},
      paginationNext: {},
    },
    display: {
      layout: [],
      pagination: {
        next: '',
        ...INITIAL_LOADING_STATE,
      },
    },
    pinnedDisplay: {
      layout: [],
      pagination: {
        next: '',
        ...INITIAL_LOADING_STATE,
      },
    },
  },
  rebuzzIn: {
    byId: {
      // [orgId/streamId/channelId/anchorItemId/streamItemId/]: {list: [], count: number, total: number}
    },
  },
  rssLinks: {
    byId: {},
    allIds: [],
    request: {},
    actions: {
      create: {
        request: {},
      },
      update: {
        request: {},
      },
    },
  },
  subscriptions: {
    byId: {},
    allIds: [],
    request: {},
    actions: {
      create: {
        request: {},
      },
    },
  },
  prevPath: '',
  isShownNewBuzzNotification: false,
  lockInterval: {
    value: '',
    isVisible: false,
  },
  sortOption: '',
  isShownCommentsModeration: {},
  scrollPositionItemId: null,
  isAscendingParameter: false,
  isActiveWalletInItems: {},
  commentableUsers: {
    isVisibleUsersModerator: false,
    itemId: '',
    users: [],
  },
  threadItems: {
    type: {
      // [anchorItemId]: THREAD_ITEMS_TYPE
    },
    boost: {
      // [anchorItemId]: []
    },
    rebuzzing: {
      // [anchorItemId]: []
    },
    customize: {
      // [anchorItemId]: [streamItemId1, streamItemId2]
    },
    frozen: {
      // [anchorItemId]: []
    },
  },
  bulkAction: {
    name: null,
    step: null,
    selected: {
      byId: {
        // [streamItemId]: boolean
      },
      threads: {
        // [anchorItemId]: {
        //    [streamItemId]: boolean
        //    length: number
        // }
      },
      layout: [],
    },
  },
};

export const ItemsSlice = createSlice({
  name: 'items',
  initialState: INITIAL_STATE,
  reducers: {
    setBulkAction: (state, { payload }) => {
      const { name, step } = payload || {};

      if (!payload) {
        state.bulkAction.selected = {
          byId: {},
          threads: {},
          layout: [],
        };
      }

      state.bulkAction.name = name;
      state.bulkAction.step = step;
    },
    setDisplayLayouts: (state, { payload }) => {
      state.items.display.layout = payload;
    },
    checkBulkSelection: (state, { payload }) => {
      handleCheckBulkSelection(state, payload);
    },
    clearLayout: state => {
      state.items.display = {
        layout: [],
        pagination: {
          next: '',
          ...INITIAL_LOADING_STATE,
        },
      };
      state.items.pinnedDisplay = {
        layout: [],
        pagination: {
          next: '',
          ...INITIAL_LOADING_STATE,
        },
      };
    },
    activateWalletInItem: (state, { payload }) => {
      const { cidKey, status } = payload;

      state.isActiveWalletInItems[cidKey] = status;
    },
    reorderItemCIDs: (state, { payload }) => {
      const { destination, source, streamItemId } = payload;

      if (!destination) return;

      const anchorItemId = state.items.display.layout.findIndex(
        item => item.id === streamItemId,
      );
      const { itemCIDs } = state.items.display.layout[anchorItemId];
      const [reorderLayout] = itemCIDs.splice(source.index, 1);
      itemCIDs.splice(destination.index, 0, reorderLayout);
      // TODO: Update the thread order on the backed by doing the API call
    },
    setInterval: (state, { payload }) => {
      const { interval, streamItemId } = payload;
      const itemCIDKey = generateItemCIDKey(payload);

      const pinnedItem = state.items.display.layout.find(
        item => item.id === streamItemId,
      );
      const newLayout = state.items.display.layout.filter(
        item => item.id !== streamItemId,
      );
      newLayout.unshift(pinnedItem);

      state.items.item.byId[itemCIDKey].metadata.pinInterval = interval;
      state.items.display.layout = newLayout;
    },
    setArchiveInterval: (state, { payload }) => {
      const itemCIDKey = generateItemCIDKey(payload);

      state.items.item.byId[itemCIDKey].metadata.archiveInterval =
        payload.interval;
    },
    setArchive: (state, { payload }) => {
      const newLayout = state.items.display.layout.filter(
        item => item.id !== payload,
      );

      state.items.display.layout = newLayout;
    },
    setUnarchived: (state, { payload }) => {
      const itemCIDKey = generateItemCIDKey(payload);

      state.items.item.byId[itemCIDKey].metadata.archiveInterval = null;
      state.items.item.byId[itemCIDKey].metadata.isArchived = false;
    },
    updateItemBounty: (state, { payload }) => {
      const { bountyPurse } = payload;
      const itemCIDKey = generateItemCIDKey(payload);

      const { bounty, userId } = bountyPurse;
      const bountyItem = state.items.item.byId[itemCIDKey].bountyPurse;
      const foundIndexBounty = state.items.item.byId[
        itemCIDKey
      ].bountyPurse.findIndex(donate => donate.userId === userId);

      if (foundIndexBounty >= 0) {
        bountyItem[foundIndexBounty].bounty.coins += bounty.coins;
        bountyItem[foundIndexBounty].bounty.currency += bounty.currency;
      } else {
        bountyItem.unshift(bountyPurse);
      }
    },
    setPrevPath: (state, { payload }) => {
      state.prevPath = payload;
    },
    changeCommentCount: (state, { payload }) => {
      state.items.item.byId[payload.itemCIDKey].metadata.commentCount =
        payload.count;
    },
    setLockedCoinsAndCurrency: (state, { payload }) => {
      const { coins, currency, settingCoins, settingCurrency } = payload;
      const itemCIDKey = generateItemCIDKey(payload);

      if (settingCoins >= 0) {
        // TODO: update lines once backend is ready for extra coins
      }

      if (settingCurrency >= 0) {
        // TODO: update lines once backend is ready for extra currency
      }

      if (coins >= 0) {
        state.items.item.byId[itemCIDKey].lockedCoins = coins;
      }

      if (currency >= 0) {
        state.items.item.byId[itemCIDKey].metadata.unlockPrice = currency;
      }

      state.items.item.byId[itemCIDKey].metadata.locked = true;
    },
    setSortOption: (state, { payload }) => {
      state.sortOption = payload;
    },
    setIsVisibleCommentsModeration: (state, { payload }) => {
      if (!payload.status) {
        delete state.isShownCommentsModeration[payload.key];
        return;
      }

      state.isShownCommentsModeration[payload.key] = payload.status;
    },
    setIsVisibleUserModerator: (state, { payload }) => {
      state.commentableUsers.isVisibleUsersModerator = payload;
    },
    setItemIdWhichCanCommentUser: (state, { payload }) => {
      state.commentableUsers.itemId = payload;
    },
    setUsersWhichCanComment: (state, { payload }) => {
      state.commentableUsers.users = payload;
    },
    setScrollPositionItemId: (state, { payload }) => {
      state.scrollPositionItemId = payload;
    },
    setIsAscendingParameter: state => {
      state.isAscendingParameter = !state.isAscendingParameter;
    },
    setLockIntervalValue: (state, { payload }) => {
      state.lockInterval.value = payload;
    },
    setLockIntervalVisibility: (state, { payload }) => {
      state.lockInterval.isVisible = payload;
    },
    setThreadItemsType: (state, { payload }) => {
      state.threadItems.type[payload.anchorItemId] = payload.type;
    },
    setBoostThreadItem: (state, { payload }) => {
      state.threadItems.boost[payload.anchorItemId] = payload.streamItemId;
    },
    setRebuzzThreadItems: (state, { payload }) => {
      state.threadItems.rebuzzing[payload.anchorItemId] = payload.itemCIDs;
    },
    setCustomizeThreadItems: (state, { payload }) => {
      state.threadItems.customize[payload.anchorItemId] = payload.threadItemIds;
    },
    checkCustomizeThreadItems: (state, { payload }) => {
      const { anchorItemId, streamItemId } = payload;
      const customizeItems = state.threadItems.customize[anchorItemId];

      if (customizeItems.includes(streamItemId)) {
        state.threadItems.customize[anchorItemId] = customizeItems.filter(
          item => item !== streamItemId,
        );
      } else {
        customizeItems.push(streamItemId);
      }
    },
    applyBoostTag: (state, { payload }) => {
      const { endorsement, promotion, content, ...itemCID } = payload;
      const itemCIDKey = generateItemCIDKey(itemCID);

      state.items.item.byId[itemCIDKey].metadata = {
        ...state.items.item.byId[itemCIDKey].metadata,
        boosts: {
          endorsement,
          promotion,
          content,
        },
      };
    },
    setItemEndorse: (state, { payload }) => {
      const { endorseCount, ...itemCID } = payload;
      const itemCIDKey = generateItemCIDKey(itemCID);

      state.items.item.byId[itemCIDKey].metadata.endorsedCount = endorseCount;
    },
    // TODO: remove this and apply an extra reducer to fetch freezing from backend once API is ready
    setFrozenThread: (state, { payload }) => {
      const { itemCIDKey } = payload;
      const hashtag = 'Frozen';
      const currentItem = state.items.item.byId[itemCIDKey];
      const { freezeThreadOn } = currentItem.metadata;

      if (!currentItem.moderationTags) {
        currentItem.moderationTags = [];
      }

      currentItem.metadata.freezeThreadOn = !freezeThreadOn;
      currentItem.moderationTags = freezeThreadOn
        ? currentItem.moderationTags.filter(tag => tag !== hashtag)
        : [...currentItem.moderationTags, hashtag];
    },
    resetThreadItems: (state, { payload }) => {
      const { anchorItemId } = payload;

      delete state.threadItems.type[anchorItemId];
      delete state.threadItems.rebuzzing[anchorItemId];
      delete state.threadItems.customize[anchorItemId];
      delete state.threadItems.boost[anchorItemId];
    },
    resetItemsSlice: () => INITIAL_STATE,
    setItemsLayout: (state, { payload }) => {
      state.items.display.layout = payload;
    },
  },
  extraReducers: {
    ...changeItemCommentableExtraReducers,
    ...addItemReactionExtraReducers,
    ...changeItemPrivacyExtraReducer,
    ...changeItemRebuzzableExtraReducers,
    ...createItemExtraReducers,
    ...editItemReactionExtraReducers,
    ...fetchItemExtraReducers,
    ...fetchItemRenderedExtraReducers,
    ...fetchTimelineExtraReducers,
    ...fetchItemsExtraReducers,
    ...fetchLayoutExtraReducers,
    ...fetchPinnedItemsExtraReducers,
    ...itemPinnedExtraReducers,
    ...addItemBookmarkExtraReducers,
    ...removeItemBookmarkExtraReducers,
    ...checkForNewBuzzesExtraReducers,
    ...removeItemExtraReducers,
    ...updateItemExtraReducers,
    ...createSubscriptionExtraReducers,
    ...fetchSubscriptionExtraReducers,
    ...createRssLinkExtraReducers,
    ...fetchRSSLinkExtraReducers,
    ...fetchItemLockedContentExtraReducers,
  },
});

export const selectorGetItem = (
  state,
  { orgId, streamId, channelId, streamItemId },
) =>
  state.items.items.item.byId[
    generateItemCIDKey({ orgId, streamId, channelId, streamItemId })
  ] || initialItemData;

export const selectorGetRawItem = createSelector(
  state => state.items.items,
  items => items.item,
);

export const selectorGetAllIdsItems = state =>
  state.items.items.item.allIds || [];

export const selectorGetPrevPathItems = state => state.items.prevPath || [];

export const selectorGetItemRequest = (
  state,
  { orgId, streamId, channelId, streamItemId },
) =>
  state.items.items.item.request[
    generateItemCIDKey({ orgId, streamId, channelId, streamItemId })
  ] || { ...INITIAL_LOADING_STATE };

export const selectorGetItemUpdateRequest = (
  state,
  { orgId, streamId, channelId, streamItemId },
) =>
  state.items.items.item.actions.update.request[
    generateItemCIDKey({ orgId, streamId, channelId, streamItemId })
  ] || { ...INITIAL_LOADING_STATE };
export const selectorGetRerenderItem = (
  state,
  { orgId, streamId, channelId, streamItemId },
) =>
  state.items.items.rendered.byId[
    generateItemCIDKey({ orgId, streamId, channelId, streamItemId })
  ] || '';
export const selectorIsItemContentLocked = (
  state,
  { orgId, streamId, channelId, streamItemId },
) =>
  state.items.items.lockedContent.byId[
    generateItemCIDKey({ orgId, streamId, channelId, streamItemId })
  ];
export const selectorIsShownCommentsModeration = (
  state,
  { orgId, streamId, channelId, streamItemId, anchorItemId },
) =>
  state.items.isShownCommentsModeration[
    generateThreadItemId({
      orgId,
      streamId,
      channelId,
      streamItemId,
      anchorItemId,
    })
  ];
export const selectorGetBoostThreadItem = (state, anchorItemId) =>
  state.items.threadItems.boost[anchorItemId];
export const selectorGetRebuzzingThreadItems = (state, anchorItemId) =>
  state.items.threadItems.rebuzzing[anchorItemId] || [];
export const selectorGetCustomizeThreadItems = (state, anchorItemId) =>
  state.items.threadItems.customize[anchorItemId] || [];
export const selectorGetThreadItemsType = (state, anchorItemId) =>
  state.items.threadItems.type[anchorItemId];
export const selectorGetItemLayout = state => state.items.items.display.layout;
export const selectorGetUpdatedILayoutItem = (state, itemCID) =>
  state.items.items.updatedILayoutItem[generateItemCIDKey(itemCID)];
export const selectorDisplayItems = state => state.items.items.display;
export const selectorPinnedDisplayItems = state =>
  state.items.items.pinnedDisplay;
export const selectorCommentableUsers = state => state.items.commentableUsers;
export const selectorScrollPositionItemId = state =>
  state.items.scrollPositionItemId;
export const selectorAscendingParameter = state =>
  state.items.isAscendingParameter;
export const selectorGetLayout = (state, layoutCIDKey) =>
  state.items.items.layouts.byId[layoutCIDKey] || [];
export const selectorGetLayoutRequest = (state, layoutCIDKey) =>
  state.items.items.layouts.request[layoutCIDKey] || {
    ...INITIAL_LOADING_STATE,
  };
export const selectorGetLayoutPaginationNext = (state, layoutCIDKey) =>
  state.items.items.layouts.paginationNext[layoutCIDKey] || '';
export const selectorIsShownNewBuzzNotification = state =>
  state.items.isShownNewBuzzNotification;
export const selectorItemsSortOption = state => state.items.sortOption;
export const selectorActiveItemWallet = (state, cidKey) =>
  state.items.isActiveWalletInItems[cidKey];
export const selectorGetSubscriptions = (state, subscriptionCIDKey) =>
  state.items.subscriptions.byId[subscriptionCIDKey] || initialSubscriptions;
export const selectorGetRssItem = (state, rssCIDKey) =>
  state.items.rssLinks.byId[rssCIDKey] || initialRssFeed;
export const selectorLockInterval = state => state.items.lockInterval;

export const selectorGetSearchItem = createSelector(
  [selectorGetItem, selectorSearchById],
  (item, searchItem) => ({
    ...item,
    content: {
      ...item.content,
      ...(searchItem && {
        snippet: searchItem.rendered,
      }),
    },
  }),
);

export const selectorGetRebuzzIn = (state, uid) =>
  state.items.rebuzzIn.byId[uid] || initialRebuzzIn;

export const selectorBulkActionName = state => state.items.bulkAction.name;
export const selectorBulkActionStep = state => state.items.bulkAction.step;

export const selectorBulkLayout = state =>
  state.items.bulkAction.selected.layout;

const selectorBulkSelectedById = state => state.items.bulkAction.selected.byId;

export const selectorBulkSelectedThreads = state =>
  state.items.bulkAction.selected.threads;

export const selectorFindItemInBulkLayout = (state, { streamItemId }) =>
  state.items.bulkAction.selected.layout.find(item => item.id === streamItemId);

export const selectorBulkSelectedTags = createSelector(
  [selectorBulkSelectedById, selectorBulkSelectedThreads, selectorBulkLayout],
  (selected, threads, layout) =>
    layout.reduce((acc, layoutItem) => {
      if (layoutItem.itemCIDs) {
        const selectedItem = layoutItem.itemCIDs.reduce((accItem, item) => {
          if (threads[layoutItem.id]?.[item.streamItemId]) {
            accItem.push({
              ...item,
              anchorItemId: layoutItem.id,
              type: ITEM_SELECT_TYPE.ITEM,
            });
          }
          return accItem;
        }, []);

        acc.push(...selectedItem);
      } else if (selected[layoutItem.id]) {
        acc.push({
          orgId: layoutItem.orgId,
          streamId: layoutItem.streamId,
          channelId: layoutItem.channelId,
          streamItemId: layoutItem.id,
          type: ITEM_SELECT_TYPE.BUZZ,
        });
      }

      return acc;
    }, []),
);

export const selectorGetCheckBulkSelection = createSelector(
  [
    (state, { streamItemId }) => streamItemId,
    (state, { anchorItemId }) => anchorItemId,
    selectorBulkLayout,
    selectorBulkSelectedThreads,
    selectorBulkSelectedById,
    selectorFindItemInBulkLayout,
  ],
  (
    streamItemId,
    anchorItemId,
    layout,
    threads,
    selected,
    currentBulkLayout,
  ) => {
    if (streamItemId === anchorItemId) {
      const bulkSelectedThread = threads[anchorItemId] || {};

      const quantityBulkSelectedItemInThread = bulkSelectedThread.length || 0;
      const totalLengthCurrentBulkLayout =
        currentBulkLayout?.itemCIDs.length || 0;

      return {
        checked:
          totalLengthCurrentBulkLayout > 0 &&
          totalLengthCurrentBulkLayout === quantityBulkSelectedItemInThread,
        indeterminate:
          quantityBulkSelectedItemInThread > 0 &&
          quantityBulkSelectedItemInThread < totalLengthCurrentBulkLayout,
      };
    }

    if (anchorItemId) {
      return {
        checked: !!threads[anchorItemId]?.[streamItemId],
        indeterminate: false,
      };
    }

    return {
      checked: !!selected[streamItemId],
      indeterminate: false,
    };
  },
);

export const {
  setDisplayLayouts,
  setBulkAction,
  checkBulkSelection,
  activateWalletInItem,
  changeCommentCount,
  clearLayout,
  reorderItemCIDs,
  setArchive,
  setArchiveInterval,
  setInterval,
  setLockedCoinsAndCurrency,
  setPrevPath,
  setSortOption,
  setUnarchived,
  updateItemBounty,
  setIsVisibleCommentsModeration,
  setIsVisibleUserModerator,
  setItemIdWhichCanCommentUser,
  setUsersWhichCanComment,
  setScrollPositionItemId,
  setIsAscendingParameter,
  setLockIntervalValue,
  setLockIntervalVisibility,
  setThreadItemsType,
  setBoostThreadItem,
  setRebuzzThreadItems,
  setCustomizeThreadItems,
  checkCustomizeThreadItems,
  resetThreadItems,
  applyBoostTag,
  setFrozenThread,
  setItemEndorse,
  resetItemsSlice,
  setItemsLayout,
} = ItemsSlice.actions;

export default ItemsSlice.reducer;
